import { motion } from "framer-motion";
import { Phone, Mail, Instagram, MessageCircle, MapPin, ArrowRight } from "lucide-react";
import { TEST_IDS } from "@/lib/testIds";
import {
  EMAIL,
  INSTAGRAM_HANDLE,
  INSTAGRAM_URL,
  PHONE_DISPLAY,
  WHATSAPP_NUMBER,
  waLink,
} from "@/lib/contact";

export const Contact = () => {
  return (
    <section
      id="iletisim"
      className="relative bg-mesh-dark text-white py-24 md:py-32 overflow-hidden"
    >
      {/* decorative blobs */}
      <div className="blob" style={{ width: 420, height: 420, top: -120, left: -120, background: "radial-gradient(circle, rgba(16,185,129,0.35), transparent 70%)" }} />
      <div className="blob delay-2" style={{ width: 480, height: 480, bottom: -160, right: -120, background: "radial-gradient(circle, rgba(14,165,233,0.3), transparent 70%)" }} />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* LEFT */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <p className="label-eyebrow mb-4 text-[#10B981]">04 — İletişim</p>
            <h2 className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.98]">
              Seninle <br />
              <span className="bg-gradient-to-r from-[#10B981] via-[#0AB7C5] to-[#0EA5E9] bg-clip-text text-transparent">
                çalışalım.
              </span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-white/75 max-w-xl">
              Sağlıklı beslenmeye ve sağlıklı yaşamaya bir adım daha yaklaş!
            </p>

            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-2xl">
              <a
                href={`tel:+${WHATSAPP_NUMBER}`}
                data-testid={TEST_IDS.contact.phoneLink}
                className="flex items-center gap-4 p-5 rounded-2xl bg-white/5 border border-white/10 hover:border-[#10B981] hover:bg-white/10 transition-all duration-300 group"
              >
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#10B981] to-[#0EA5E9] flex items-center justify-center shrink-0">
                  <Phone className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-white/50">Telefon</p>
                  <p className="font-heading font-extrabold text-sm md:text-base group-hover:text-[#10B981] transition-colors truncate">
                    {PHONE_DISPLAY}
                  </p>
                </div>
              </a>
              <a
                href={`mailto:${EMAIL}`}
                data-testid={TEST_IDS.contact.emailLink}
                className="flex items-center gap-4 p-5 rounded-2xl bg-white/5 border border-white/10 hover:border-[#0EA5E9] hover:bg-white/10 transition-all duration-300 group"
              >
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#0EA5E9] to-[#10B981] flex items-center justify-center shrink-0">
                  <Mail className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-white/50">E-posta</p>
                  <p className="font-heading font-extrabold text-sm md:text-base group-hover:text-[#0EA5E9] transition-colors truncate">
                    {EMAIL}
                  </p>
                </div>
              </a>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={TEST_IDS.contact.instagramLink}
                className="flex items-center gap-4 p-5 rounded-2xl bg-white/5 border border-white/10 hover:border-[#10B981] hover:bg-white/10 transition-all duration-300 group"
              >
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#10B981] to-[#0EA5E9] flex items-center justify-center shrink-0">
                  <Instagram className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-white/50">Instagram</p>
                  <p className="font-heading font-extrabold text-sm md:text-base group-hover:text-[#10B981] transition-colors truncate">
                    {INSTAGRAM_HANDLE}
                  </p>
                </div>
              </a>
              <div className="flex items-center gap-4 p-5 rounded-2xl bg-white/5 border border-white/10">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#0EA5E9] to-[#10B981] flex items-center justify-center shrink-0">
                  <MapPin className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-white/50">Konum</p>
                  <p className="font-heading font-extrabold text-sm md:text-base">İstanbul · Online & Yüz Yüze</p>
                </div>
              </div>
            </div>

            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={TEST_IDS.contact.waBigBtn}
              className="btn-shimmer mt-12 w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-gradient-to-r from-[#25D366] to-[#1ebd5b] text-white px-9 py-5 rounded-full font-extrabold text-sm uppercase tracking-[0.18em] hover:-translate-y-0.5 hover:shadow-[0_24px_50px_-18px_rgba(37,211,102,0.6)] transition-all duration-300"
            >
              <MessageCircle className="w-5 h-5" />
              WhatsApp&apos;tan Randevu Al
              <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>

          {/* RIGHT — Quote panel */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-5"
          >
            <div className="relative h-full rounded-3xl border border-white/15 p-10 md:p-14 overflow-hidden bg-gradient-to-br from-white/[0.04] to-transparent flex items-center">
              <div className="absolute top-0 right-0 w-2 h-24 bg-gradient-to-b from-[#10B981] to-[#0EA5E9]" />
              <div className="absolute bottom-0 left-0 w-24 h-2 bg-gradient-to-r from-[#0EA5E9] to-[#10B981]" />
              <div>
                <p className="label-eyebrow text-[#10B981] mb-6">Manifesto</p>
                <p className="font-heading font-black text-3xl md:text-4xl lg:text-5xl text-white leading-[1.1]">
                  &ldquo;Sağlığınızı{" "}
                  <span className="bg-gradient-to-r from-[#10B981] to-[#0EA5E9] bg-clip-text text-transparent">
                    klinik bilimle
                  </span>{" "}
                  koruyun, performansınızı{" "}
                  <span className="bg-gradient-to-r from-[#0EA5E9] to-[#10B981] bg-clip-text text-transparent">
                    global vizyonla
                  </span>{" "}
                  zirveye taşıyın.&rdquo;
                </p>
                <p className="mt-8 text-[10px] font-extrabold uppercase tracking-[0.28em] text-white/55">
                  — Berke Akdağ, Diyetisyen
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
